import { useEffect, useId, useRef, useState } from "react";
import type { PartyCommand, PartyView, Profile, WorldSnapshot } from "@frontier/protocol/contracts";
import { stationDefinition } from "@frontier/content/stations";
import { HULLS } from "@frontier/content/definitions";
import { GalaxyMapButton } from "./GalaxyMapButton";
import { AccountButton } from "./AccountButton";
import { HelpButton } from "./HelpButton";
import { ChatButton } from "./ChatButton";
import { PartyControls } from "./PartyControls";
import { credits } from "./ui-labels";

export type StationTab = "contracts" | "equipment" | "shop" | "season";
interface Props {
  tab: StationTab; onTab: (tab: StationTab) => void;
  profile: Profile; world: WorldSnapshot; party: PartyView; targetId: string; connected: boolean;
  onParty: (command: PartyCommand) => void;
  resumeIntro: boolean; personalTraining: boolean; departDisabled: boolean; freeFlight: boolean;
  onLaunch: () => void; onOpenGalaxy: () => void; onAccount: () => void; onChat: () => void;
  onHelp?: (() => void) | undefined; onShop?: (() => void) | undefined; chatUnread: number;
}
const compact = new Intl.NumberFormat("ru-RU", { notation: "compact", maximumFractionDigits: 1 });
export function StationHeader(props: Props) {
  const { profile, party, tab, onTab, resumeIntro, personalTraining } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null), trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!menuOpen) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !menu.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [menuOpen]);
  useEffect(() => setMenuOpen(false), [profile.playerId, profile.runId]);
  const open = (action: () => void) => { setMenuOpen(false); action(); };
  const tabs: { id: StationTab; label: string; disabled: boolean }[] = [
    { id: "contracts", label: "КОНТРАКТЫ", disabled: false },
    { id: "equipment", label: "АНГАР", disabled: personalTraining && !profile.tutorial?.rewardGranted },
    { id: "shop", label: "МАГАЗИН", disabled: resumeIntro },
    { id: "season", label: "СЕЗОН", disabled: personalTraining },
  ];
  return <header className="dock-header station-header">
    <div className="dock-brand" title={`${stationDefinition(profile.stationId ?? "meridian").name} · ${HULLS[profile.hullId].name}`}>
      <span>СТАНЦИЯ</span><b>{stationDefinition(profile.stationId ?? "meridian").name.toUpperCase()}</b>
      <small>{HULLS[profile.hullId].name}</small>
    </div>
    <div className="dock-wallet">
      <button className="dock-resource dock-resource-credits" onClick={() => { if (props.onShop) open(props.onShop); }} disabled={!props.onShop}
        aria-label={`${credits(profile.credits)} кредитов. Пополнить`} title={`${credits(profile.credits)} КР`}>
        <b><span className="wallet-full">{credits(profile.credits)}</span><span className="wallet-compact">{compact.format(BigInt(profile.credits))}</span> <small>КР</small></b>
      </button>
      <button className="dock-resource dock-resource-components" onClick={() => { if (props.onShop) open(props.onShop); }} disabled={!props.onShop}
        aria-label={`${profile.components.toLocaleString("ru-RU")} компонентов. Пополнить`} title={`${profile.components.toLocaleString("ru-RU")} КОМП.`}>
        <b><span className="wallet-full">{profile.components.toLocaleString("ru-RU")}</span><span className="wallet-compact">{compact.format(profile.components)}</span> <small>КОМП.</small></b>
      </button>
    </div>
    <nav className="hangar-tabs dock-tabs" aria-label="Подготовка к отстыковке">
      {tabs.map(item => <button key={item.id} aria-current={tab === item.id ? "page" : undefined} disabled={item.disabled} onClick={() => { setMenuOpen(false); onTab(item.id); }}>{item.label}
        {item.id === "equipment" && profile.lastRunResult?.contracts.some(result => result.moduleUnlock || result.equipmentUnlock !== "none") && <i />}
      </button>)}
    </nav>
    <div className="dock-tools" ref={menu} data-menu-open={menuOpen} onKeyDown={event => {
      if (event.key === "Escape" && menuOpen && !menu.current?.querySelector('.party-button[aria-expanded="true"]')) {
        event.preventDefault(); event.stopPropagation(); setMenuOpen(false); trigger.current?.focus();
      }
    }}>
      <button className="dock-menu-toggle" ref={trigger} aria-label={`Меню станции${props.chatUnread ? ` · ${props.chatUnread} непрочитанных сообщений` : ""}${party.invitations.length ? ` · ${party.invitations.length} приглашений` : ""}`}
        aria-expanded={menuOpen} aria-controls={menuId} onClick={() => setMenuOpen(value => !value)}>
        <span aria-hidden="true">☰</span><small>МЕНЮ</small>{(props.chatUnread > 0 || party.invitations.length > 0) && <i>{Math.min(99, props.chatUnread + party.invitations.length)}</i>}
      </button>
      <div className="dock-tool-buttons" id={menuId}>
        <div className="dock-tool-item"><GalaxyMapButton onOpen={() => open(props.onOpenGalaxy)} /><span>Карта галактики</span></div>
        {props.onHelp && <div className="dock-tool-item"><HelpButton onOpen={() => { if (props.onHelp) open(props.onHelp); }} /><span>Помощь и обратная связь</span></div>}
        <div className="dock-tool-item"><AccountButton factionId={profile.factionId} onOpen={() => open(props.onAccount)} /><span>Пилот и фракция</span></div>
        <div className="dock-tool-item"><ChatButton unread={props.chatUnread} onOpen={() => open(props.onChat)} /><span>Чат{props.chatUnread ? ` · ${props.chatUnread}` : ""}</span></div>
        <div className="dock-tool-item"><PartyControls key={menuOpen ? "menu-open" : "menu-closed"} view={party} world={props.world} playerId={profile.playerId} targetId={props.targetId} connected={props.connected} onCommand={props.onParty} />
          <span>Группа · {party.party?.members.length ?? 0}/4{party.invitations.length ? ` · приглашений: ${party.invitations.length}` : ""}</span></div>
      </div>
    </div>
    <button className="launch-button dock-depart" disabled={props.departDisabled} onClick={props.onLaunch} aria-label="Отстыковаться" title={props.freeFlight ? "Свободный вылет" : undefined}><span>ВЫЛЕТ</span></button>
  </header>;
}
